import type { IconName } from "@openanalyst-ai/ui/icon"
import { base64Encode } from "@openanalyst-ai/core/util/encode"
import analyticsMeasurementReadinessCard from "@/assets/starter-projects/analytics-measurement-readiness-card.png"
import ecommerceAdsAnalysisCard from "@/assets/starter-projects/ecommerce-ads-analysis-card.png"
import emailRevenueSystemCard from "@/assets/starter-projects/email-revenue-system-card.png"
// HIDDEN starter (not live): import facebookAdsPerformanceReviewCard from "@/assets/starter-projects/facebook-ads-performance-review-card.png"
import funnelConversionAnalysisCard from "@/assets/starter-projects/funnel-conversion-analysis-card.png"
// HIDDEN starter (not live): import googleAdsGrowthReviewCard from "@/assets/starter-projects/google-ads-growth-review-card.png"
// HIDDEN starter (not live): import leadGenerationSystemCard from "@/assets/starter-projects/lead-generation-system-card.png"
import openanalystShopifyManagerCard from "@/assets/starter-projects/openanalyst-shopify-manager-card.png"
// HIDDEN starter (not live): import paidAdsCreativeStrategyCard from "@/assets/starter-projects/paid-ads-creative-strategy-card.png"
// HIDDEN starter (not live): import seoAuthorityGrowthSystemCard from "@/assets/starter-projects/seo-authority-growth-system-card.png"
// HIDDEN starter (not live): import youtubeSocialGrowthSystemCard from "@/assets/starter-projects/youtube-social-growth-system-card.png"

export type StarterPanelIcon = IconName | "search" | "wrench"
export type ProjectStarterTemplateID =
  | "ai-marketing-workspace"
  | "email-revenue-system"
  | "ecommerce-ads-analysis"
  | "google-ads-growth-review"
  | "facebook-ads-performance-review"
  | "paid-ads-creative-strategy"
  | "funnel-conversion-analysis"
  | "lead-generation-system"
  | "seo-authority-growth-system"
  | "youtube-social-growth-system"
  | "analytics-measurement-readiness"
  | "openanalyst-shopify-manager"
  | "job-research"

export const PROJECT_STARTER_TEMPLATE_IDS = [
  "ai-marketing-workspace",
  "email-revenue-system",
  "ecommerce-ads-analysis",
  "google-ads-growth-review",
  "facebook-ads-performance-review",
  "paid-ads-creative-strategy",
  "funnel-conversion-analysis",
  "lead-generation-system",
  "seo-authority-growth-system",
  "youtube-social-growth-system",
  "analytics-measurement-readiness",
  "openanalyst-shopify-manager",
  "job-research",
] as const satisfies readonly ProjectStarterTemplateID[]

export const STARTER_PROJECTS_FOLDER = "OpenAnalyst Projects"
export const DEFAULT_STARTER_PROJECT_ROOT_LABEL = `~/Documents/${STARTER_PROJECTS_FOLDER}`

export function defaultStarterProjectRoot(home: string | undefined) {
  return defaultStarterProjectRootPath(home) ?? DEFAULT_STARTER_PROJECT_ROOT_LABEL
}

export function defaultStarterProjectRootPath(home: string | undefined) {
  const base = home?.replace(/[\\/]+$/, "")
  if (!base) return undefined
  return `${base}/Documents/${STARTER_PROJECTS_FOLDER}`
}

export function starterProjectRootPath(configured: string | undefined, home: string | undefined) {
  return configured?.trim() || defaultStarterProjectRootPath(home)
}

export function starterProjectRootLabel(configured: string | undefined, home: string | undefined) {
  return configured?.trim() || defaultStarterProjectRoot(home)
}

export function isUnknownStarterProjectTemplateError(message: string) {
  return message.includes("Unknown starter project template:")
}

export function isProjectStarterTemplateID(value: string | null | undefined): value is ProjectStarterTemplateID {
  return PROJECT_STARTER_TEMPLATE_IDS.includes(value as ProjectStarterTemplateID)
}

export type PromptStarterAction = {
  kind: "prompt"
  prompt: string
}

export type CreateProjectStarterAction = {
  kind: "createProject"
  templateId: ProjectStarterTemplateID
}

export type StarterPanelItem = {
  id: string
  title: string
  description?: string
  detail?: string
  icon?: StarterPanelIcon
  image?: string
  actionLabel?: string
  action: PromptStarterAction | CreateProjectStarterAction
  source?: "memory" | "default" | "config"
  updatedAt?: number
}

export type StarterPanelSection = {
  id: string
  title: string
  layout?: "card-grid" | "list"
  items: StarterPanelItem[]
}

export type StarterPanelView = {
  version: 1
  surface: "session"
  source?: "memory" | "default" | "mixed"
  sections: StarterPanelSection[]
}

type Language = {
  t(key: string): string
}

type StarterClient = {
  project?: {
    starter?(input: { surface: "session"; limit: number }): Promise<{ data?: StarterPanelView }>
  }
}

export function defaultSessionStarterPanel(language: Language): StarterPanelView {
  return {
    version: 1,
    surface: "session",
    source: "default",
    sections: [
      {
        id: "defaults",
        title: language.t("session.new.starter.label"),
        layout: "card-grid",
        items: [
          {
            id: "explore",
            icon: "folder",
            title: language.t("session.new.starter.explore.title"),
            description: language.t("session.new.starter.explore.description"),
            action: { kind: "prompt", prompt: language.t("session.new.starter.explore.prompt") },
            source: "default",
          },
          {
            id: "next-task",
            icon: "task",
            title: language.t("session.new.starter.nextTask.title"),
            description: language.t("session.new.starter.nextTask.description"),
            action: { kind: "prompt", prompt: language.t("session.new.starter.nextTask.prompt") },
            source: "default",
          },
          {
            id: "checks",
            icon: "check",
            title: language.t("session.new.starter.checks.title"),
            description: language.t("session.new.starter.checks.description"),
            action: { kind: "prompt", prompt: language.t("session.new.starter.checks.prompt") },
            source: "default",
          },
        ],
      },
    ],
  }
}

export function defaultProjectTemplateStarterPanel(): StarterPanelView {
  return {
    version: 1,
    surface: "session",
    source: "default",
    sections: [
      {
        id: "project-templates",
        title: "Start a project",
        layout: "card-grid",
        items: [
          {
            id: "ai-marketing-workspace",
            icon: "sparkles",
            title: "AI Marketing Workspace",
            description:
              "Turn a website and business goal into a grounded diagnostic, strategy, and approval-ready 30-day plan.",
            detail: "Guided marketing",
            actionLabel: "Create",
            action: { kind: "createProject", templateId: "ai-marketing-workspace" },
            source: "default",
          },
          {
            id: "openanalyst-shopify-manager",
            icon: "shopify",
            image: openanalystShopifyManagerCard,
            title: "OpenAnalyst Shopify Manager",
            description:
              "Connect Shopify, review revenue, spot campaign issues, and prepare safe updates.",
            detail: "Best first",
            actionLabel: "Create",
            action: { kind: "createProject", templateId: "openanalyst-shopify-manager" },
            source: "default",
          },
          {
            id: "job-research",
            icon: "search",
            title: "OpenAnalyst Job Research",
            description:
              "Research roles, companies, and markets — track openings, compare offers, and build an application plan.",
            detail: "Career research",
            actionLabel: "Create",
            action: { kind: "createProject", templateId: "job-research" },
            source: "default",
          },
          {
            id: "email-revenue-system",
            icon: "receipt-text",
            image: emailRevenueSystemCard,
            title: "Email Revenue System",
            description: "Plan email campaigns, review playbooks, and draft approval-ready memos.",
            detail: "Revenue workflow",
            actionLabel: "Create",
            action: { kind: "createProject", templateId: "email-revenue-system" },
            source: "default",
          },
          {
            id: "ecommerce-ads-analysis",
            icon: "storefront",
            image: ecommerceAdsAnalysisCard,
            title: "Ecommerce Ads Analysis",
            description:
              "Find growth leaks across ads, product economics, creative, offers, and funnel steps.",
            detail: "Growth audit",
            actionLabel: "Create",
            action: { kind: "createProject", templateId: "ecommerce-ads-analysis" },
            source: "default",
          },
          /* HIDDEN starter (not live) — uncomment this item (and its image import) to show it again:
          {
            id: "google-ads-growth-review",
            icon: "ads",
            image: googleAdsGrowthReviewCard,
            title: "Google Ads Growth Review",
            description:
              "Audit search intent, keywords, campaign structure, landing pages, and experiments.",
            detail: "Search ads",
            actionLabel: "Create",
            action: { kind: "createProject", templateId: "google-ads-growth-review" },
            source: "default",
          },
          */
          /* HIDDEN starter (not live) — uncomment this item (and its image import) to show it again:
          {
            id: "facebook-ads-performance-review",
            icon: "meta-ads",
            image: facebookAdsPerformanceReviewCard,
            title: "Facebook Ads Performance Review",
            description: "Check creative fatigue, audiences, offers, and scaling risks before you spend more.",
            detail: "Paid social",
            actionLabel: "Create",
            action: { kind: "createProject", templateId: "facebook-ads-performance-review" },
            source: "default",
          },
          */
          {
            id: "funnel-conversion-analysis",
            icon: "activity",
            image: funnelConversionAnalysisCard,
            title: "Funnel Conversion Analysis",
            description: "Locate conversion leaks across landing pages, checkout, demo flow, objections, and proof.",
            detail: "Conversion",
            actionLabel: "Create",
            action: { kind: "createProject", templateId: "funnel-conversion-analysis" },
            source: "default",
          },
          /* HIDDEN starter (not live) — uncomment this item (and its image import) to show it again:
          {
            id: "lead-generation-system",
            icon: "users",
            image: leadGenerationSystemCard,
            title: "Lead Generation System",
            description: "Map ICP capture, lead magnets, forms, nurture, qualification, and pipeline handoff.",
            detail: "Lead pipeline",
            actionLabel: "Create",
            action: { kind: "createProject", templateId: "lead-generation-system" },
            source: "default",
          },
          */
          {
            id: "analytics-measurement-readiness",
            icon: "analytics",
            image: analyticsMeasurementReadinessCard,
            title: "Analytics Measurement Readiness",
            description: "Check tracking, UTMs, event coverage, attribution gaps, and dashboards.",
            detail: "Measurement",
            actionLabel: "Create",
            action: { kind: "createProject", templateId: "analytics-measurement-readiness" },
            source: "default",
          },
          /* HIDDEN starters (not live) — uncomment these items (and their image imports) to show them again:
          {
            id: "seo-authority-growth-system",
            icon: "magnifying-glass",
            image: seoAuthorityGrowthSystemCard,
            title: "SEO Authority Growth System",
            description:
              "Audit keywords, technical issues, competitors, backlinks, and 10xKB action backlog.",
            detail: "SEO growth",
            actionLabel: "Create",
            action: { kind: "createProject", templateId: "seo-authority-growth-system" },
            source: "default",
          },
          {
            id: "paid-ads-creative-strategy",
            icon: "photo",
            image: paidAdsCreativeStrategyCard,
            title: "Paid Ads Creative Strategy",
            description: "Turn hooks, angles, proof, and offers into a clear creative testing plan.",
            detail: "Creative testing",
            actionLabel: "Create",
            action: { kind: "createProject", templateId: "paid-ads-creative-strategy" },
            source: "default",
          },
          {
            id: "youtube-social-growth-system",
            icon: "panels-top-left",
            image: youtubeSocialGrowthSystemCard,
            title: "YouTube Social Growth System",
            description: "Review content pillars, hooks, retention, posting cadence, and channel growth.",
            detail: "Content growth",
            actionLabel: "Create",
            action: { kind: "createProject", templateId: "youtube-social-growth-system" },
            source: "default",
          },
          */
        ],
      },
    ],
  }
}

export function mergeStarterPanel(panel: StarterPanelView | undefined, defaults: StarterPanelView): StarterPanelView {
  const items = panel?.sections.flatMap((section) => section.items) ?? []
  if (items.length === 0) return defaults
  const fallback = defaults.sections
    .flatMap((section) => section.items)
    .filter((item) => !items.some((next) => next.id === item.id))
  return {
    version: 1,
    surface: "session",
    source: items.length === 3 ? "memory" : "mixed",
    sections: [
      {
        id: panel?.sections[0]?.id ?? defaults.sections[0]?.id ?? "starters",
        title: panel?.sections[0]?.title ?? defaults.sections[0]?.title ?? "Suggested starts",
        layout: panel?.sections[0]?.layout ?? defaults.sections[0]?.layout ?? "card-grid",
        items: [...items, ...fallback].slice(0, 3),
      },
    ],
  }
}

// Curated FIRST-RUN prompt per starter — the message auto-sent in the onboarding
// first-run dialog so a brand-new user sees an immediate, concrete "wow" result for
// the project they picked (no manual typing). Each is written to produce something
// useful on the first turn even before external apps are connected, and to clearly
// ask to connect when live data is needed. Falls back to a generic kickoff.
const FIRST_RUN_PROMPTS: Partial<Record<ProjectStarterTemplateID, string>> = {
  "ai-marketing-workspace":
    "This is the first guided run for my AI Marketing Workspace. Run /start-marketing-workspace and read workspace/project-status.md before doing anything else. If durable work already exists, validate it and resume from the next permitted transition. Otherwise ask for only the missing website or business description, primary outcome, target customer, and active channels, one short question at a time, and continue without integrations when they are unavailable or skipped. Produce grounded, evidence-labelled workspace artifacts and an editable first-week plan, propose rather than schedule work, and stop for explicit review before any calendar, Symphony, or external action.",
  "openanalyst-shopify-manager":
    "I just set up this Shopify workspace. Give me a fast, concrete first look: summarize what you can already see from my project, use the Shopify app's skills/scripts or connected core tools when available, report store revenue, order count, AOV and any low-stock or campaign issues. If Shopify isn't connected yet, tell me exactly how to connect it in one short step, then outline the 3 highest-impact things you'd do for my store this week.",
  "job-research":
    "Kick off my job research. Find relevant, recent (last 30 days) roles for me, summarize the top 5 with company, location and why they fit, and lay out a simple application plan for this week. If you need my target role or location, ask me one short question first, then proceed.",
  "email-revenue-system":
    "Start my email revenue system. Draft a 30-day email plan (campaigns + lifecycle flows) tailored to my business, with subject-line angles and a simple sending calendar. Ask me one quick question about my product/audience if you need it, then produce the plan.",
  "ecommerce-ads-analysis":
    "Run a first growth-leak scan for my ecommerce business across ads, product economics, creative, offers and funnel steps. Give me a prioritized list of the biggest likely leaks and the checks to confirm each. If you need account data, tell me which app to connect, then continue with what you can reason about now.",
  "funnel-conversion-analysis":
    "Do a first conversion-leak pass on my funnel — landing page, checkout, demo flow, objections and proof. Give me a prioritized list of likely drop-off points and the exact fix to test for each. Ask me for my site URL or funnel steps if you need them, then proceed.",
  "analytics-measurement-readiness":
    "Run a measurement-readiness check: tracking setup, UTMs, event coverage, attribution gaps and dashboards. Give me a clear readiness scorecard with the top gaps to fix first. Ask me which analytics stack I use if needed, then produce the scorecard.",
}

// Shared preamble: the agent runs the first task INSIDE the onboarding dialog and
// must gather any missing credentials inline (minimal questions), then use the
// available tools to connect and run — never sending the user to Settings.
const FIRST_RUN_PREAMBLE =
  "You are running the user's very first task inside a focused onboarding dialog, so be fast and concrete. " +
  "If a step needs an app or integration that isn't connected yet, do NOT tell the user to open Settings or Integrations — " +
  "instead ask them inline for the MINIMAL thing you need (for example a store URL, an API key, or an access token), one short question at a time, " +
  "then use the app's available skills, scripts, core tools, and any connect or credential tools it exposes to apply it and continue the run. " +
  "Ask only what is strictly required to produce a useful first result and assume sensible defaults for everything else. " +
  "If you genuinely cannot proceed without something only the user has, ask for that one thing clearly, then wait for their reply."

export function firstRunPromptForTemplate(templateId: ProjectStarterTemplateID | null | undefined): string {
  if (templateId === "ai-marketing-workspace") return FIRST_RUN_PROMPTS[templateId] ?? ""
  const body =
    (templateId ? FIRST_RUN_PROMPTS[templateId] : undefined) ??
    "Give me a fast, concrete first result for this project: summarize what you can already see here, then propose the 3 highest-impact things to do next. If you need anything connected, ask me inline for the minimal credential and connect it with your tools."
  return `${FIRST_RUN_PREAMBLE}\n\n${body}`
}

export function starterProjectFirstRunPath(directory: string, templateId: ProjectStarterTemplateID) {
  return `/${base64Encode(directory)}/session?onboardingIntro=1&starterTemplate=${encodeURIComponent(templateId)}`
}

export function starterPanelItemForTemplate(templateId: ProjectStarterTemplateID): StarterPanelItem | null {
  return defaultProjectTemplateStarterPanel()
    .sections.flatMap((section) => section.items)
    .find((item) => item.action.kind === "createProject" && item.action.templateId === templateId) ?? null
}

export async function loadSessionStarterPanel(client: StarterClient | undefined) {
  const response = await client?.project?.starter?.({ surface: "session", limit: 3 }).catch(() => undefined)
  return response?.data
}
